import { useRef, useEffect, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useBlockSlot,
  useTokens,
  SectionContextProvider,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { getContrastTextColor } from '../utils/luminance';

/** Form card surface — subtitle contrast is computed against this. */
const FORM_CARD_BG = '#1a1a1a';

// ─── Slot group definitions ───────────────────────────────────────────────────

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a strong full-bleed background photo for a contact form section — interiors, exteriors, or amenity spaces that work behind a dark overlay. Skip logos, floor plans, parking lots, utility areas, and other low-value shots.',
    context:
      'Background behind the Home page contact / lead form (Pinned Callouts). Must not reuse the Home page hero background image.',
  },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function PinnedCallouts() {
  const bg = useSlotGroup('pinnedcallouts.bg_image', bgImageDef);
  const formSlot = useBlockSlot('pinnedcallouts.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  // Black or white against the dark form card — used for subtitle + form copy.
  const formTextColor = getContrastTextColor(FORM_CARD_BG);

  // Match TabbedCallouts CTA hover: secondary fill + contrast label.
  const tokens = useTokens();
  const { ctaHoverBg, ctaHoverText } = useMemo(() => {
    const secondary = tokens?.color?.secondary || '#3E441D';
    return {
      ctaHoverBg: secondary,
      ctaHoverText: getContrastTextColor(secondary),
    };
  }, [tokens]);

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();

  const sectionRef = useRef<HTMLElement>(null);
  const parallaxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const parallax = parallaxRef.current;
    if (!section || !parallax) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rafId = 0;
    let active = false;

    const tick = () => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = (vh - rect.top) / (vh + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      const offset = (clamped - 0.5) * 80;
      parallax.style.transform = `translateY(${offset}px)`;
      if (active) rafId = requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        active = entry.isIntersecting;
        if (active) rafId = requestAnimationFrame(tick);
        else cancelAnimationFrame(rafId);
      },
      { threshold: 0, rootMargin: '200px' },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="pc-section relative overflow-hidden min-h-[480px] md:min-h-[640px] flex items-center justify-center"
    >
      {/* Parallax background */}
      <div
        ref={parallaxRef}
        className="absolute left-0 right-0 z-0"
        style={{ top: '-20%', bottom: '-20%', willChange: 'transform' }}
      >
        <div className="absolute inset-0">
          <EditableSlotGroup groupPath="pinnedcallouts.bg_image" definition={bgImageDef}>
            {bg.image ? (
              <VibesitesImage
                url={bg.image}
                alt={bg.alt || ''}
                sizes="100vw"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-text)]" />
            )}
          </EditableSlotGroup>
        </div>
      </div>

      {/* Soft vignette so the photo stays visible around the form */}
      <div className="absolute inset-0 z-[1] pointer-events-none bg-black/25" />

      {/* Right-aligned contact form card (inset from the edge) */}
      <div
        ref={contentRef}
        className="reveal-fade-up relative z-10 w-full px-4 sm:px-6 lg:pl-12 lg:pr-24 xl:pr-32 py-16 md:py-24 flex justify-center lg:justify-end"
      >
        <div
          className="pc-form-card w-full max-w-[560px]"
          style={{
            ['--pc-form-text' as string]: formTextColor,
            ['--pc-form-text-muted' as string]: `color-mix(in srgb, ${formTextColor} 72%, transparent)`,
            ['--pc-form-border' as string]: `color-mix(in srgb, ${formTextColor} 85%, transparent)`,
            ['--pc-cta-hover-bg' as string]: ctaHoverBg,
            ['--pc-cta-hover-text' as string]: ctaHoverText,
          }}
        >
          <RichTextContent
            path="pinnedcallouts.title"
            label="Title"
            defaultValue="<p>Have Questions?</p>"
            as="h2"
            className="pc-form-title"
          />
          <RichTextContent
            path="pinnedcallouts.subtitle"
            label="Subtitle"
            defaultValue="<p>Fill out the form below, drop us a line, or stop by to learn how you can start living in Roxanne</p>"
            as="p"
            className="pc-form-subtitle"
          />

          <EditableToken path="pinnedcallouts.form_block" as="div" className="pc-form-block">
            {formSlot ? (
              <SectionContextProvider sectionId={formSlot.sectionId}>
                <formSlot.BlockComponent sectionId={formSlot.sectionId} />
              </SectionContextProvider>
            ) : (
              <p className="pc-form-empty">No form block selected</p>
            )}
          </EditableToken>
        </div>
      </div>

      <style>{`
        .pc-form-card {
          background: #1a1a1a;
          padding: 2rem 1.75rem 2.25rem;
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);
        }
        @media (min-width: 768px) {
          .pc-form-card {
            padding: 2.75rem 3rem 3rem;
          }
        }

        .pc-form-title {
          font-family: Georgia, 'Times New Roman', serif;
          font-size: 1.75rem;
          font-weight: 400;
          line-height: 1.2;
          color: var(--pc-form-text, #ffffff);
          text-align: left;
          margin: 0 0 0.75rem;
        }
        @media (min-width: 768px) {
          .pc-form-title {
            font-size: 2.125rem;
          }
        }
        .pc-form-title p {
          margin: 0;
        }

        .pc-form-subtitle {
          font-family: var(--vibe-font-body);
          font-size: 0.9375rem;
          line-height: 1.55;
          color: var(--pc-form-text, #ffffff);
          text-align: left;
          margin: 0 0 1.75rem;
        }
        .pc-form-subtitle p {
          margin: 0;
        }

        .pc-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--pc-form-text-muted, rgba(255, 255, 255, 0.72));
          text-align: center;
          margin: 0;
          padding: 1.5rem 0;
        }

        /* Hide the lead-form's own header — we render title/subtitle above */
        .pc-form-block .mh-lead-form-card > h3,
        .pc-form-block .mh-lead-form-card > p {
          display: none !important;
        }

        /* Transparent card shell — outer .pc-form-card provides the dark panel */
        .pc-form-block .mh-lead-form-card {
          background: transparent !important;
          background-color: transparent !important;
          box-shadow: none !important;
          border-radius: 0 !important;
          padding: 0 !important;
          max-width: none !important;
        }

        .pc-form-block .mh-lead-form-card form {
          display: grid !important;
          grid-template-columns: 1fr 1fr;
          gap: 1.35rem 1.5rem !important;
        }

        .pc-form-block .mh-lead-form-card form > * {
          grid-column: 1 / -1;
          min-width: 0;
        }

        /* Email + phone sit side-by-side on desktop */
        .pc-form-block .mh-lead-form-card form > .mh-lead-form-name-row + div {
          grid-column: 1;
        }
        .pc-form-block .mh-lead-form-card form > .mh-lead-form-name-row + div + div {
          grid-column: 2;
        }

        @media (max-width: 639px) {
          .pc-form-block .mh-lead-form-card form {
            grid-template-columns: 1fr;
          }
          .pc-form-block .mh-lead-form-card form > .mh-lead-form-name-row + div,
          .pc-form-block .mh-lead-form-card form > .mh-lead-form-name-row + div + div {
            grid-column: 1;
          }
          .pc-form-block .mh-lead-form-name-row,
          .pc-form-block .mh-lead-form-field-row {
            grid-template-columns: 1fr !important;
          }
        }

        /* Underline field style — black/white text for contrast on the card */
        .pc-form-block .mh-lead-form-card input:not([type="checkbox"]):not([type="date"]),
        .pc-form-block .mh-lead-form-card select,
        .pc-form-block .mh-lead-form-card textarea {
          background: transparent !important;
          background-color: transparent !important;
          border: none !important;
          border-bottom: 1px solid var(--pc-form-border, rgba(255, 255, 255, 0.85)) !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          color: var(--pc-form-text, #ffffff) !important;
          -webkit-text-fill-color: var(--pc-form-text, #ffffff) !important;
          padding: 0.35rem 0 0.55rem !important;
          height: auto !important;
          min-height: 2.5rem;
          font-size: 0.9375rem !important;
        }

        .pc-form-block .mh-lead-form-card input:not([type="checkbox"]):not([type="date"])::placeholder,
        .pc-form-block .mh-lead-form-card textarea::placeholder {
          color: var(--pc-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
          -webkit-text-fill-color: var(--pc-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
          opacity: 1;
          font-size: 0.8125rem;
        }

        .pc-form-block .mh-lead-form-card select {
          padding-right: 1.75rem !important;
        }
        .pc-form-block .mh-lead-form-card select option {
          color: #111;
          background: #fff;
          -webkit-text-fill-color: #111;
        }

        .pc-form-block .mh-lead-form-card textarea {
          min-height: 4.5rem;
          resize: vertical;
        }

        .pc-form-block .mh-lead-form-card input:focus,
        .pc-form-block .mh-lead-form-card select:focus,
        .pc-form-block .mh-lead-form-card textarea:focus {
          outline: none !important;
          border-bottom-color: var(--pc-form-text, #ffffff) !important;
        }

        /* Date field: match full-width underline of sibling fields */
        .pc-form-block .mh-lead-form-date-field {
          background: color-mix(in srgb, var(--pc-form-text, #ffffff) 6%, transparent);
          padding: 0;
          border-bottom: 1px solid var(--pc-form-border, rgba(255, 255, 255, 0.85));
        }
        .pc-form-block .mh-lead-form-date-field input[type="date"] {
          background: transparent !important;
          background-color: transparent !important;
          border: none !important;
          border-bottom: none !important;
          border-radius: 0 !important;
          box-shadow: none !important;
          padding: 0.35rem 2rem 0.55rem 0.65rem !important;
          height: auto !important;
          min-height: 2.5rem;
          font-size: 0.9375rem !important;
          /* Empty (required + invalid): hide native date ghost text so the
             custom "Desired Move-in Date" label is the only visible copy */
          color: transparent !important;
          -webkit-text-fill-color: transparent !important;
        }
        .pc-form-block .mh-lead-form-date-field input[type="date"]:focus {
          outline: none !important;
        }
        .pc-form-block .mh-lead-form-date-field:focus-within {
          border-bottom-color: var(--pc-form-text, #ffffff);
        }
        .pc-form-block .mh-lead-form-date-field input[type="date"]:valid {
          color: var(--pc-form-text, #ffffff) !important;
          -webkit-text-fill-color: var(--pc-form-text, #ffffff) !important;
        }
        .pc-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit,
        .pc-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit-fields-wrapper,
        .pc-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit-text,
        .pc-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit-month-field,
        .pc-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit-day-field,
        .pc-form-block .mh-lead-form-date-field input[type="date"]::-webkit-datetime-edit-year-field {
          color: transparent;
          -webkit-text-fill-color: transparent;
          padding: 0;
        }
        .pc-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit,
        .pc-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit-fields-wrapper,
        .pc-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit-text,
        .pc-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit-month-field,
        .pc-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit-day-field,
        .pc-form-block .mh-lead-form-date-field input[type="date"]:valid::-webkit-datetime-edit-year-field {
          color: var(--pc-form-text, #ffffff);
          -webkit-text-fill-color: var(--pc-form-text, #ffffff);
        }
        .pc-form-block .mh-lead-form-date-field > span {
          left: 0.65rem !important;
          color: var(--pc-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
          font-size: 0.8125rem !important;
        }

        .pc-form-block .mh-lead-form-card svg {
          color: var(--pc-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
          stroke: var(--pc-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
          right: 0.35rem !important;
        }

        /* Override block inline grays / dark text that fail on the dark card */
        .pc-form-block .mh-lead-form-card [style*="color: rgb(107, 114, 128)"],
        .pc-form-block .mh-lead-form-card [style*="color: #6b7280"],
        .pc-form-block .mh-lead-form-card [style*="color: #9ca3af"],
        .pc-form-block .mh-lead-form-card [style*="color: #374151"],
        .pc-form-block .mh-lead-form-card [style*="color: #111827"],
        .pc-form-block .mh-lead-form-card label,
        .pc-form-block .mh-lead-form-card label span,
        .pc-form-block .mh-lead-form-card p:not([role="alert"]) {
          color: var(--pc-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
        }

        /* Submit — outline in contrast text, left-aligned */
        .pc-form-block .mh-lead-form-submit-button {
          width: auto !important;
          max-width: none !important;
          align-self: start;
          justify-self: start;
          margin-top: 0.35rem;
          background: transparent !important;
          background-color: transparent !important;
          color: var(--pc-form-text, #ffffff) !important;
          border: 1px solid var(--pc-form-text, #ffffff) !important;
          border-radius: 0 !important;
          padding: 0.7rem 1.75rem !important;
          font-size: 0.8125rem !important;
          font-weight: 500 !important;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          transition: background-color 0.25s ease, color 0.25s ease;
        }
        .pc-form-block .mh-lead-form-submit-button:hover:not(:disabled) {
          background: var(--pc-cta-hover-bg) !important;
          border-color: var(--pc-cta-hover-bg) !important;
          color: var(--pc-cta-hover-text) !important;
        }

        .pc-form-block .mh-lead-form-card a {
          color: var(--pc-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
        }

        /* Success state on dark panel */
        .pc-form-block .mh-lead-form-card [role="status"] h3 {
          color: var(--pc-form-text, #ffffff) !important;
        }
        .pc-form-block .mh-lead-form-card [role="status"] p {
          color: var(--pc-form-text-muted, rgba(255, 255, 255, 0.72)) !important;
        }
        .pc-form-block .mh-lead-form-success-cta {
          background: transparent !important;
          border: 1px solid var(--pc-form-text, #ffffff) !important;
          color: var(--pc-form-text, #ffffff) !important;
          border-radius: 0 !important;
        }

        @media (prefers-reduced-motion: reduce) {
          .pc-section [style*="will-change"] {
            transform: none !important;
          }
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PinnedCallouts',
  displayName: 'Pinned Callouts',
  tokens: {
    'pinnedcallouts.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Have Questions?</p>',
    },
    'pinnedcallouts.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Fill out the form below, drop us a line, or stop by to learn how you can start living in Roxanne</p>',
    },
    'pinnedcallouts.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
  },
  slotGroups: {
    'pinnedcallouts.bg_image': bgImageDef,
  },
};

(PinnedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PinnedCallouts;
